import { Loader } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import { AxiosProxy } from '../api/axiosProxy/axiosProxy';
import { useFetchManifestUser } from '../api/user/useFetchManifestUser';
import AgreementComponent from '../components/Agreement/Agreement';
import { useOrganizationAgreement } from '../hooks/organization/useOrganizationAgreement';
import { useUserAgreementAcceptance } from '../hooks/organization/useUserAgreementAcceptance';
import { useAuth } from '../hooks/useAuth';
import { useOrganizationId } from '../hooks/utils/useOrganizationId';
import i18n from '../i18n';
import { Logger } from '../lib/sentry/captureExceptionWithMessage/captureExceptionWithMessage';
import { UserMenu } from '../shared-portal/Layout/UserMenu/UserMenu';
import styles from './AuthedLayout.module.scss';
import Footer from './Footer';
import { ManifestLogo } from './Icons/ManifestLogoIcon/ManifestLogoIcon';
import LeftNav from './LeftNav/LeftNav';
import Toolbar from './Toolbar/Toolbar';
import { TopAibomBanner, TopLMCOBanner } from './TopBanner/TopBanner';
import TopBar from './TopBar';
import Loading from './Loading';

export const AuthedLayout = () => {
  const { user: savedUser, login } = useAuth();
  const {
    data: fetchedUser,
    error: fetchUserError,
    isLoading: isLoadingUser,
  } = useFetchManifestUser();
  const { pathname } = useLocation();
  const outlet = useOutlet();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLeftSidebarExpanded, setIsLeftSidebarExpanded] = useState(false);
  const user = savedUser || fetchedUser;
  const toggleExpandSidebar = () => {
    setIsLeftSidebarExpanded(!isLeftSidebarExpanded);
  };
  const [currentOrgId, setCurrentOrgId] = useOrganizationId();
  const queryClient = useQueryClient();

  // State to track acceptance processing
  const [isAccepting, setIsAccepting] = useState(false);
  const [hasAcceptedAgreement, setHasAcceptedAgreement] = useState(false);
  // Fetch organization agreement details (enriched with domains/parentData)
  const {
    data: agreementData,
    isLoading: isAgreementLoading,
    isSuccess: isSuccessOrganizationAgreement,
  } = useOrganizationAgreement(currentOrgId?.toString());

  const rootDomains = agreementData?.data?.domains || [];
  const rootHasLMCO = rootDomains.includes('lmco.com');

  const isLMCO =
    isSuccessOrganizationAgreement &&
    agreementData.data?.requiresAgreement &&
    rootHasLMCO;

  // Fetch user's agreement acceptance status - only when we have orgId and agreement data
  const shouldFetchUserAcceptance = Boolean(currentOrgId) && !isAgreementLoading;
  const { data: userAcceptanceData, isLoading: isUserAcceptanceLoading } =
    useUserAgreementAcceptance(shouldFetchUserAcceptance);

  const [isLanguageReady, setIsLanguageReady] = useState(false);  // Check for specific subscription types.
  const currentOrgSubscriptions = fetchedUser?.organization?.subscriptions || {};
  const hasOnlyAibomSubscription = ("aibom" in currentOrgSubscriptions && currentOrgSubscriptions.aibom) && !currentOrgSubscriptions.sbom;

  useEffect(() => {
    if (fetchedUser?.locale) {
      i18n
        .changeLanguage(fetchedUser.locale)
        .then(() => {
          setIsLanguageReady(true);
        })
        .catch((error) => {
          Logger.captureExceptionWithMessage(
            'Error setting language in AuthedLayout',
            error,
          );
          setIsLanguageReady(true); // Proceed even if language change fails
        });
    } else {
      setIsLanguageReady(true);
    }
  }, [fetchedUser?.locale]);

  useEffect(() => {
    if (fetchedUser && !isEqual(savedUser, fetchedUser)) {
      login?.(fetchedUser, { shouldRedirect: false });
    }
  }, [fetchedUser, savedUser, login]);
  useEffect(() => {
    document.documentElement.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);

  // Fallback logic: if user has organizations but no currentOrgId is set, use the first organization
  useEffect(() => {
    if (user && user.hasOrganizations && !currentOrgId && user.organizations && user.organizations.length > 0) {
      const firstOrgId = user.organizations[0]?.id;
      if (firstOrgId) {
        console.info('No organization ID set, falling back to first organization:', firstOrgId);
        setCurrentOrgId(firstOrgId);
      }
    }
  }, [user, currentOrgId, setCurrentOrgId]);

  const useDetailedLoaders = true; // UI experiment - enables more detailed loading messages instead of generic circular loaders. Per Tiff, okay to enable for now.

  if (isLoadingUser || !isLanguageReady) {
    console.info('Loading user & locale data...');
    return (
      <div aria-label="loading-user-language-subscription" className={styles.loaderContainer}>
        {useDetailedLoaders ? (
          <Loading customLoadingMessage={'Fetching user data...'} />
        ) : (
          <Loader className="status-loader" speed="slow" size={18} />
        )}
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Safety check: If we have a user but no organization ID available, prevent infinite loading
  if (user && !currentOrgId && user.hasOrganizations) {
    console.warn('User has organizations but currentOrgId is undefined - indefinite loading may occur.');

    // Log with Sentry for better visibility
    Logger.captureExceptionWithMessage(
      'User has organizations but currentOrgId is undefined',
      new Error('currentOrgId is undefined while user has organizations'),
    )

    return (
      <div aria-label="missing-organization-id" className={styles.loaderContainer}>
        {useDetailedLoaders ? (
          <Loading customLoadingMessage={'Fetching organizations...'} />
        ) : (
          <Loader className="status-loader" speed="slow" size={18} />
        )}
      </div>
    );
  }

  if (isAgreementLoading || isUserAcceptanceLoading) {
    console.info('Loading agreement or user acceptance data...');
    return (
      <div aria-label="loading-user-agreement-acceptance" className={styles.loaderContainer}>
        {useDetailedLoaders ? (
          <Loading customLoadingMessage={'Checking user agreements...'} />
        ) : (
          <Loader className="status-loader" speed="slow" size={18} />
        )}
      </div>
    );
  }

  const requiresAgreement = agreementData?.data?.requiresAgreement;
  const agreementText = agreementData?.data?.agreementText || '';
  const agreementLastUpdated = agreementData?.data?.agreementLastUpdated;
  const organizationName = agreementData?.data?.organizationName;
  const userAcceptedAt = userAcceptanceData?.data?.agreementAcceptedAt;

  const needsToAcceptAgreement =
    requiresAgreement &&
    (!userAcceptedAt ||
      (agreementLastUpdated &&
        new Date(agreementLastUpdated) > new Date(userAcceptedAt)));

  if (needsToAcceptAgreement && !hasAcceptedAgreement) {
    return (
      <AgreementComponent
        agreementText={agreementText}
        orgName={organizationName}
        onAccept={async () => {
          // Update user's acceptance via PUT endpoint
          try {
            setIsAccepting(true);
            await AxiosProxy.put({
              url: 'organization/member/agreement',
              body: { agreementAcceptedAt: new Date().toISOString() },
            });
            // Invalidate user agreement acceptance query
            queryClient.invalidateQueries(['userAgreementAcceptance']);
            setHasAcceptedAgreement(true);
          } catch (error) {
            Logger.captureExceptionWithMessage(
              'Error updating agreement acceptance',
              error,
            );
          } finally {
            setIsAccepting(false);
          }
        }}
        isAccepting={isAccepting}
      />
    );
  }

  if (!user.hasOrganizations) {
    return (
      <>
        <Toolbar
          withSidebar={false}
          isSidebarOpen={false}
          logo={<ManifestLogo height={30} width={120} />}
          rightSide={
            <UserMenu
              onLogOut={() => {
                navigate('/logout?reason=user-manual');
              }}
              texts={{
                logOut: t('navigation.logout.label'),
              }}
            />
          }
          actions={{
            goBack: () => navigate(-1),
            goBackLabel: t('global.goBack'),
            goForward: () => navigate(1),
            goForwardLabel: t('global.goForward'),
          }}
        />
        <div data-testid="empty-page-non-org-user" />
      </>
    );
  }

  if (!user.isPaidOrgs && !pathname.includes('vendor-portal')) {
    return <Navigate to="/logout?reason=vendor-email-link-only" />;
  }

  return (
    <>
      {isLMCO && <TopLMCOBanner />}
      {hasOnlyAibomSubscription && <TopAibomBanner />}

      <div
        className={`app-layout ${isLeftSidebarExpanded && 'left-sidebar-expanded'} ${isLMCO ? 'is-lcmo' : ''
          } ${hasOnlyAibomSubscription ? 'is-aibom-only' : ''}`}
      >
        <LeftNav
          isPaidOrg={user.isPaidOrgs}
          toggleExpandSidebar={toggleExpandSidebar}
          withTopBanner={isLMCO}
          subscriptions={fetchedUser?.organization?.subscriptions || {}}
        />
        <TopBar isPaidOrg={user.isPaidOrgs} />
        <div className="page-content-area">
          {outlet}
          <Footer />
        </div>
      </div>
    </>
  );
};

export default AuthedLayout;
